import { useMessageSource } from "react-message-source";
import { useFormatNumber } from "../../../pages/shared/hooks/useFormatNumber";
import { ProductRefType } from "../../../context/product-context/types";

interface ProductCost {
  firstYearCost: string;
  afterFirstYearCost: string;
}

interface ProductSpecificProperties {
  /**
   * Given a product this function will return it's corresponding miles and more points.
   *
   * @param productRef The product reference.
   * @return string The Miles and More points.
   */
  getMilesAndMore: (productRef: ProductRefType) => string;

  /**
   * Given a product ref this function will returns the product cost by year.
   *
   * @param productRef The product reference.
   * @return string The cost per year.
   */
  getProductCost: (productRef: ProductRefType) => ProductCost;
}

export default function useProductSpecificProperties(): ProductSpecificProperties {
  const { formatNumberAsAmount } = useFormatNumber();
  const { getMessage } = useMessageSource();

  const getFormattedAmount = (amount: number): string => {
    if (amount === 0) {
      return getMessage("Product.pricing.free");
    } else {
      return formatNumberAsAmount(amount, { withCurrency: true });
    }
  };

  const getMilesAndMore = (productRef: ProductRefType): string => {
    let value = null;
    switch (productRef) {
      case ProductRefType.SWISS_SILVER:
        value = 1250;
        break;
      case ProductRefType.SWISS_GOLD:
        value = 2500;
        break;
      case ProductRefType.SWISS_PLATINUM:
        value = 5000;
        break;
    }
    return value
      ? formatNumberAsAmount(value, { thousandSeparator: ".", suffix: "" })
      : null;
  };

  const getProductCost = (productRef: ProductRefType): ProductCost => {
    let result: { firstYearCost: number; afterFirstYearCost: number } = null;
    switch (productRef) {
      case ProductRefType.SWISS_SILVER:
        result = {
          firstYearCost: 60,
          afterFirstYearCost: 120,
        };
        break;
      case ProductRefType.SWISS_GOLD:
        result = {
          firstYearCost: 110,
          afterFirstYearCost: 220,
        };
        break;
      case ProductRefType.SWISS_PLATINUM:
        result = {
          firstYearCost: 350,
          afterFirstYearCost: 700,
        };
        break;
      case ProductRefType.CS_DUO_SILVER:
        result = {
          firstYearCost: 0,
          afterFirstYearCost: 100,
        };
        break;
      case ProductRefType.CS_DUO_GOLD:
        result = {
          firstYearCost: 0,
          afterFirstYearCost: 200,
        };
        break;
      case ProductRefType.CS_MASTERCARD_BUSINESS_STANDARD_CHF:
        result = {
          firstYearCost: 0,
          afterFirstYearCost: 100,
        };
        break;
      case ProductRefType.CS_MASTERCARD_BUSINESS_GOLD_CHF:
        result = {
          firstYearCost: 0,
          afterFirstYearCost: 200,
        };
        break;
      case ProductRefType.CS_MASTERCARD_BUSINESS_EURO:
        result = {
          firstYearCost: 0,
          afterFirstYearCost: 200,
        };
        break;
      case ProductRefType.SC_MASTERCARD_BUSINESS_SILVER_CHF:
        result = {
          firstYearCost: 0,
          afterFirstYearCost: 50,
        };
        break;
      case ProductRefType.SC_MASTERCARD_BUSINESS_GOLD_CHF:
        result = {
          firstYearCost: 0,
          afterFirstYearCost: 150,
        };
        break;
      case ProductRefType.SC_MASTERCARD_BUSINESS_GOLD_EUR:
        result = {
          firstYearCost: 0,
          afterFirstYearCost: 150,
        };
        break;
      case ProductRefType.SC_MASTERCARD_BUSINESS_CORPORATE_USD:
        result = {
          firstYearCost: 0,
          afterFirstYearCost: 200,
        };
        break;
      default:
      /*  result = {
          firstYearCost: 0,
          afterFirstYearCost: 0,
        }*/
    }

    return {
      firstYearCost: getFormattedAmount(result?.firstYearCost),
      afterFirstYearCost: getFormattedAmount(result?.afterFirstYearCost),
    };
  };

  return {
    getMilesAndMore,
    getProductCost,
  };
}
